import Phaser from 'phaser';
import feedbackPresetsJson from './feedback-presets.json';
import particlesPresetsJson from '../particles-presets.json';
import { isReduceMotion, REDUCED_SHAKE_FACTOR } from './motion-prefs';

/**
 * Camera shake. Default: 100ms duration, 0.005 intensity (very subtle).
 * Use larger intensity (0.01–0.02) for hits/explosions.
 */
export function shake(scene: Phaser.Scene, durationMs: number = 100, intensity: number = 0.005): void {
  scene.cameras.main.shake(durationMs, intensity);
}

/**
 * White flash on the target for 50ms.
 * - Rectangle: captures fillColor, sets white, restores.
 * - Sprite: captures current tint, sets white, restores via setTint.
 * - Graphics (e.g. boss shapes drawn via Graphics.fillPoints): alpha pulse
 *   (Graphics has no setTint — pulse 0.4 alpha then restore).
 */
export function flashWhite(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Sprite | Phaser.GameObjects.Rectangle | Phaser.GameObjects.Graphics,
): void {
  if ('setFillStyle' in target && 'fillColor' in target) {
    const rect = target as Phaser.GameObjects.Rectangle;
    const originalColor = rect.fillColor;
    rect.setFillStyle(0xffffff);
    scene.time.delayedCall(50, () => {
      if (rect.active) rect.setFillStyle(originalColor);
    });
  } else if ('setTint' in target && typeof (target as Phaser.GameObjects.Sprite).setTint === 'function') {
    const sprite = target as Phaser.GameObjects.Sprite;
    const originalTint = sprite.tintTopLeft;
    const hadTint = sprite.isTinted;
    sprite.setTint(0xffffff);
    scene.time.delayedCall(50, () => {
      if (!sprite.active) return;
      if (hadTint) sprite.setTint(originalTint); else sprite.clearTint();
    });
  } else {
    const gfx = target as Phaser.GameObjects.Graphics;
    const originalAlpha = gfx.alpha;
    gfx.setAlpha(0.4);
    scene.time.delayedCall(50, () => {
      if (gfx.active) gfx.setAlpha(originalAlpha);
    });
  }
}

/**
 * Spawn 12 Kenney particle sprites radiating outward from (x, y) and fading out over ~500ms.
 * Requires smoke_01, fire_01, spark_06, and star_05 textures to be preloaded.
 * Falls back silently to nothing if textures are missing (caller should use
 * scene.textures.exists('smoke_01') to decide whether to call this or explosionParticles).
 */
export function explosionParticlesAtlas(scene: Phaser.Scene, x: number, y: number): void {
  const particleKeys = ['smoke_01', 'fire_01', 'spark_06', 'star_05'];
  const COUNT = 12;
  const RADIUS_PX = 60;
  for (let i = 0; i < COUNT; i++) {
    const angle = (i / COUNT) * Math.PI * 2;
    const dx = Math.cos(angle) * RADIUS_PX;
    const dy = Math.sin(angle) * RADIUS_PX;
    const key = particleKeys[Math.floor(Math.random() * particleKeys.length)];
    // Guard: if texture somehow not loaded, skip this particle instead of crashing
    if (!scene.textures.exists(key)) continue;
    const sprite = scene.add.image(x, y, key)
      .setScale(0.3 + Math.random() * 0.3)
      .setAlpha(0.9);
    scene.tweens.add({
      targets: sprite,
      x: x + dx,
      y: y + dy,
      alpha: 0,
      scale: 0.1,
      duration: 500 + Math.random() * 200,
      ease: 'Cubic.easeOut',
      onComplete: () => sprite.destroy(),
    });
  }
}

/**
 * Spawn 8 small particles radiating outward from (x, y) and fading out over 300ms.
 * Used for explosions. Fallback when Kenney particle textures are not loaded.
 *
 * QA #C5 fix: default color was 0xffeb3b (yellow) which clashed with non-yellow
 * factions (e.g. red 符文門 boss explosion looked yellow). Default is now
 * 0xeceff1 (neutral light gray-white) — caller should pass faction color
 * when known (boss def faction → see _particleColorFromName / faction color map).
 */
export function explosionParticles(scene: Phaser.Scene, x: number, y: number, color: number = 0xeceff1): void {
  const COUNT = 8;
  const RADIUS_PX = 40;
  const DURATION_MS = 300;
  for (let i = 0; i < COUNT; i++) {
    const angle = (i / COUNT) * Math.PI * 2;
    const targetX = x + Math.cos(angle) * RADIUS_PX;
    const targetY = y + Math.sin(angle) * RADIUS_PX;
    const particle = scene.add.rectangle(x, y, 4, 4, color);
    scene.tweens.add({
      targets: particle,
      x: targetX,
      y: targetY,
      alpha: 0,
      duration: DURATION_MS,
      ease: 'Cubic.easeOut',
      onComplete: () => particle.destroy(),
    });
  }
}


/**
 * Per-scene hitStop ref-count. WeakMap so dead scenes get GC'd. Each
 * overlapping hitStop() bumps the counter on enter and decrements on its
 * own timer fire; only the LAST timer to drain the counter actually resumes
 * tweens / restores timeScale. Prevents QA #C2: rapid hits used to spawn
 * stacked setTimeouts; the earliest one would resume timeScale before the
 * later hits' pauses expired, making the freeze feel inconsistent.
 *
 * Exported for tests only.
 */
export const _hitStopState: WeakMap<object, { count: number }> = new WeakMap();

/**
 * Brief game-feel pause on player hit: pause all tweens + zero timeScale for durationMs.
 * Uses setTimeout (not scene.time) so it fires even with timeScale = 0.
 *
 * QA #C2 fix: ref-counted across overlapping calls — N concurrent hitStops
 * only resume when ALL N timers have fired (longest-wins), instead of the
 * first timer prematurely resuming while later hitStops still want a freeze.
 */
export function hitStop(scene: Phaser.Scene, durationMs: number = 50): void {
  const state = _hitStopState.get(scene) ?? { count: 0 };
  if (state.count === 0) {
    // First (or sole) hitStop — actually pause.
    try { scene.tweens.pauseAll(); } catch { /* test harness */ }
    try { scene.time.timeScale = 0; } catch { /* test harness */ }
  }
  state.count += 1;
  _hitStopState.set(scene, state);
  setTimeout(() => {
    const cur = _hitStopState.get(scene);
    if (!cur) return;
    cur.count -= 1;
    if (cur.count <= 0) {
      cur.count = 0;
      try { scene.tweens.resumeAll(); } catch { /* test harness */ }
      try { scene.time.timeScale = 1; } catch { /* test harness */ }
    }
  }, durationMs);
}

/**
 * Spawn a floating damage number at (x, y) that floats upward and fades out.
 * Returns the text object (for testing).
 */
export function floatingDamage(
  scene: Phaser.Scene, x: number, y: number, amount: number, color = '#ef5350',
): Phaser.GameObjects.Text {
  const txt = scene.add.text(x, y, '-' + amount, {
    fontSize: '20px', color, fontStyle: 'bold', fontFamily: 'sans-serif',
  }).setOrigin(0.5).setDepth(50);
  scene.tweens.add({
    targets: txt, y: y - 40, alpha: 0,
    duration: 700, ease: 'Cubic.easeOut',
    onComplete: () => txt.destroy(),
  });
  return txt;
}

// ============================================================================
// G01 + G04 — Trauma-based screen shake + Sparkle-style feedback preset
// Wiki spec: docs/GAME_TECHNIQUES_WIKI.md sections G01 & G04
// ============================================================================

/**
 * Single feedback preset definition (matches feedback-presets.json schema).
 * - trauma: 0-1, fed to JuiceSystem.addTrauma() for camera shake
 * - flash: color name ('white'|'red'|'yellow'|null) — visual hit flash on target
 * - particles: preset name ('spark'|'explosion'|'starburst'|null)
 * - hitPauseMs: 0 = no pause; >0 freezes scene tweens for N ms
 * - sfx: SFX key in scene._sfx ('hit'|'explode'|'ult-cast'|null)
 */
export interface FeedbackPreset {
  trauma: number;
  flash: string | null;
  particles: string | null;
  hitPauseMs: number;
  sfx: string | null;
}

/**
 * Typed view over the JSON `presets` map. Adding a new preset = add to JSON,
 * no TS change needed. boss-designer / juice-developer can A/B feel via HMR.
 */
export type FeedbackPresets = Record<string, FeedbackPreset>;

const FEEDBACK_PRESETS: FeedbackPresets = (feedbackPresetsJson as { presets: FeedbackPresets }).presets;

export function getFeedbackPreset(name: string): FeedbackPreset | null {
  return FEEDBACK_PRESETS[name] ?? null;
}

/** All known preset names — exported for test surface & call-site autocomplete. */
export function listFeedbackPresets(): string[] {
  return Object.keys(FEEDBACK_PRESETS);
}

// ============================================================================
// G07 — Particles2D-style JSON preset system
// Wiki spec: docs/GAME_TECHNIQUES_WIKI.md G07
// ============================================================================

/**
 * One particles preset — matches src/game/particles-presets.json schema.
 * Tunable in JSON by boss-designer / juice-developer; HMR reloads on save.
 *
 * - faction: 'celestial'|'runic'|'elemental'|'techno'|'dominion' (informational —
 *   used by call sites / tests to pick a preset matching the boss).
 * - amount: integer particle count per spawn.
 * - lifetimeMs: per-particle tween duration (random jitter ±20% inside spawn loop).
 * - speed: [minPxPerSec, maxPxPerSec] radial speed range.
 * - scale: [minScale, maxScale] initial scale (rectangles drawn at 4×4 base).
 * - alpha: [startAlpha, endAlpha] tween.
 * - tint: hex color string (e.g. "0xff6600") OR [colorA, colorB] random pick.
 * - spread: emission cone in radians (0 = forward beam, 2π / 6.283 = full circle).
 *   When omitted defaults to full circle.
 * - gravity: optional dy in px/sec² (positive = falls, negative = rises).
 */
export interface ParticlesPreset {
  faction: 'celestial' | 'runic' | 'elemental' | 'techno' | 'dominion';
  amount: number;
  lifetimeMs: number;
  speed: [number, number];
  scale: [number, number];
  alpha: [number, number];
  tint: string | [string, string];
  spread?: number;
  gravity?: number;
}

export type ParticlesPresets = Record<string, ParticlesPreset>;

const PARTICLES_PRESETS: ParticlesPresets =
  (particlesPresetsJson as unknown as { presets: ParticlesPresets }).presets;

export function getParticlesPreset(name: string): ParticlesPreset | null {
  return PARTICLES_PRESETS[name] ?? null;
}

/** All particles preset names — exported for tests & autocomplete. */
export function listParticlesPresets(): string[] {
  return Object.keys(PARTICLES_PRESETS);
}

/** Parse a hex-string like "0xff6600" → number. Falls back to 0xffffff on garbage. */
function _parseTintHex(s: string): number {
  if (typeof s !== 'string') return 0xffffff;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0xffffff;
}

/**
 * Resolve a preset.tint (single or [a,b]) into a single 24-bit color int.
 * For [a,b] gradient pairs we pick uniformly at random.
 */
function _resolveTint(tint: string | [string, string]): number {
  if (Array.isArray(tint)) {
    const pick = Math.random() < 0.5 ? tint[0] : tint[1];
    return _parseTintHex(pick);
  }
  return _parseTintHex(tint);
}

/**
 * Spawn particles from a JSON preset at (x, y) on the given scene.
 *
 * Spawns `amount` 4×4 rectangles radiating outward within `spread` radians
 * (random per particle), each tweened over `lifetimeMs` ± 20% to fade out.
 *
 * Headless / test-safe: every scene call (`add.rectangle`, `tweens.add`) is
 * try-wrapped so a mock scene that throws on one call doesn't abort the rest.
 *
 * Returns the spawn count actually attempted (caller can assert in tests).
 */
export function spawnParticlesFromPreset(
  scene: Phaser.Scene,
  preset: ParticlesPreset,
  x: number,
  y: number,
): number {
  if (!scene) return 0;
  const amount = Math.max(0, Math.floor(preset.amount));
  if (amount === 0) return 0;
  const [vMin, vMax] = preset.speed;
  const [sMin, sMax] = preset.scale;
  const [aStart, aEnd] = preset.alpha;
  const spread = typeof preset.spread === 'number' ? preset.spread : Math.PI * 2;
  const gravity = preset.gravity ?? 0;
  const baseLife = Math.max(1, preset.lifetimeMs);

  let spawned = 0;
  for (let i = 0; i < amount; i++) {
    // Direction: uniform within cone centred on 0 (caller's "forward").
    const angle = (Math.random() - 0.5) * spread;
    const speed = vMin + Math.random() * (vMax - vMin);
    const scale = sMin + Math.random() * (sMax - sMin);
    const tint = _resolveTint(preset.tint);
    const lifeMs = baseLife * (0.8 + Math.random() * 0.4);

    // Final position: radial + gravity-arc'd vertical.
    const dx = Math.cos(angle) * speed * (lifeMs / 1000);
    const dyRadial = Math.sin(angle) * speed * (lifeMs / 1000);
    const dyGravity = 0.5 * gravity * Math.pow(lifeMs / 1000, 2);
    const targetX = x + dx;
    const targetY = y + dyRadial + dyGravity;

    try {
      const rect = scene.add.rectangle(x, y, 4, 4, tint);
      try { rect.setScale?.(scale); } catch { /* test harness */ }
      try { rect.setAlpha?.(aStart); } catch { /* test harness */ }
      scene.tweens.add({
        targets: rect,
        x: targetX,
        y: targetY,
        alpha: aEnd,
        duration: lifeMs,
        ease: 'Cubic.easeOut',
        onComplete: () => { try { rect.destroy(); } catch { /* test harness */ } },
      });
      spawned++;
    } catch { /* test harness — keep spawning the rest */ }
  }
  return spawned;
}

/**
 * G01: Trauma-based screen shake — Godot-style accumulator with exponential
 * decay + noise-driven offset. Beats Phaser's flat random shake because
 * - small hits accumulate into bigger shakes naturally
 * - big shakes have a ~1.5s tail (餘震) instead of hard cutoff
 * - offset comes from sin-sampled "noise" so motion is continuous, not jittery
 *
 * Usage in scene:
 *   private juice = new JuiceSystem(this);
 *   create() { ... }
 *   update(time, deltaMs) {
 *     this.juice.update(deltaMs / 1000, this.cameras.main);
 *     ...
 *   }
 *   onHit() { this.juice.addTrauma(0.15); }  // small hit
 *   onBossExplode() { this.juice.addTrauma(0.6); }  // big boom
 *   onPlayerDeath() { this.juice.addTrauma(1.0); }  // max
 *
 * G04: playFeedback('bossHit') one-shot triggers the whole stack
 * (trauma + flash + particles + hit-pause + sfx) from a JSON preset.
 */
export class JuiceSystem {
  /** Current trauma 0-1. Read-only externally — use addTrauma to mutate. */
  trauma = 0;
  /** Per-second decay multiplier. trauma *= (1 - decay*dt) per frame. */
  readonly decay: number;
  /** Max camera offset at trauma=1, in pixels. shake = trauma^2 * maxOffset. */
  readonly maxOffsetX: number;
  readonly maxOffsetY: number;

  /** Owning scene (used by playFeedback for flash/sfx/particles). May be null in tests. */
  private scene: Phaser.Scene | null;
  /**
   * QA #C3 fix: previously-applied shake offset on the camera. Each tick we
   * recover the dynamic base = camera.scrollX - lastOffsetX so a scrolling
   * scene (side-scroller, follow-camera) keeps moving correctly while we
   * superimpose shake. Replaces a once-snapshotted baseScroll which went
   * stale and snapped the camera back to old position when shake ended.
   */
  private lastOffsetX = 0;
  private lastOffsetY = 0;
  /** Internal time accumulator for noise sampling (sec). Pre-bound so unit tests can seed. */
  private noiseT = 0;

  constructor(scene: Phaser.Scene | null = null, opts: { decay?: number; maxOffsetX?: number; maxOffsetY?: number } = {}) {
    this.scene = scene;
    this.decay = opts.decay ?? 0.85;
    this.maxOffsetX = opts.maxOffsetX ?? 12;
    this.maxOffsetY = opts.maxOffsetY ?? 8;
  }

  /** Add trauma. Clamps to [0, 1]. Negative amounts are ignored. */
  addTrauma(amount: number): void {
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.trauma = Math.min(1, this.trauma + amount);
  }

  /**
   * Per-frame update. dtSec = seconds since last frame. camera = the camera
   * to shake (typically scene.cameras.main). Safe to call every frame even
   * when trauma=0 (early-returns + restores scroll).
   */
  update(dtSec: number, camera: Phaser.Cameras.Scene2D.Camera | null | undefined): void {
    if (!camera) return;

    // QA #C3 fix: recover dynamic base from current scroll - last applied
    // offset. In a scrolling scene this gives the scene-controlled base
    // each tick, instead of snapshotting once and snapping back at shake-end.
    const baseX = camera.scrollX - this.lastOffsetX;
    const baseY = camera.scrollY - this.lastOffsetY;

    // Decay trauma (clamp to 0)
    if (this.trauma > 0) {
      this.trauma = Math.max(0, this.trauma - this.decay * dtSec);
    }

    // Tick noise time (continuous, never reset — wraps naturally via sin period)
    this.noiseT += dtSec;

    // Below threshold → restore base (remove any residual offset) and bail.
    if (this.trauma <= 0.001) {
      if (this.lastOffsetX !== 0 || this.lastOffsetY !== 0) {
        try { camera.setScroll(baseX, baseY); } catch { /* test harness */ }
        this.lastOffsetX = 0;
        this.lastOffsetY = 0;
      }
      return;
    }

    // Quadratic ramp — small trauma stays subtle, big trauma is dramatic
    const shakeAmt = this.trauma * this.trauma;
    // 減少晃動 (reduce-motion): damp the applied camera offset to a faint rumble
    // when the parent/accessibility toggle is on. Purely presentational — the
    // trauma accumulator itself is untouched (so feel returns instantly if the
    // flag flips), we only scale how far the camera actually moves. K-3 friendly.
    const motionFactor = isReduceMotion() ? REDUCED_SHAKE_FACTOR : 1;
    // Two-frequency sin sampling (cheap "noise" — looks continuous, not random)
    const ox = Math.sin(this.noiseT * 47.3) * this.maxOffsetX * shakeAmt * motionFactor;
    const oy = Math.sin(this.noiseT * 31.7 + 1.7) * this.maxOffsetY * shakeAmt * motionFactor;
    try { camera.setScroll(baseX + ox, baseY + oy); } catch { /* test harness */ }
    this.lastOffsetX = ox;
    this.lastOffsetY = oy;
  }

  /**
   * G04: one-call hit feedback. Reads preset by name, dispatches all
   * subsystems (trauma + flash + particles + hit-pause + sfx). Each
   * subsystem is independently try-wrapped so a missing sfx key etc.
   * never aborts the whole feedback.
   *
   * Subsystems used:
   *   - trauma → this.addTrauma
   *   - flash  → flashTarget(scene, opts.flashTarget, color) if a target given
   *   - particles → explosionParticles(scene, x, y) if opts.x/y given
   *   - hitPauseMs → hitStop(scene, ms) if > 0
   *   - sfx → (scene as any)._sfx?.play(key) if scene has _sfx
   *
   * Returns the resolved preset (or null if unknown) so callers / tests
   * can inspect what fired.
   */
  playFeedback(
    name: string,
    opts: {
      x?: number;
      y?: number;
      flashTarget?: Phaser.GameObjects.Sprite | Phaser.GameObjects.Rectangle | Phaser.GameObjects.Graphics | Phaser.GameObjects.Image | null;
      flashColor?: number;
      particleColor?: number;
    } = {},
  ): FeedbackPreset | null {
    const preset = FEEDBACK_PRESETS[name];
    if (!preset) {
      // eslint-disable-next-line no-console
      console.warn('[JuiceSystem.playFeedback] unknown preset:', name);
      return null;
    }

    // 1. Trauma (always safe — pure math)
    if (preset.trauma > 0) this.addTrauma(preset.trauma);

    const scene = this.scene;
    if (!scene) return preset;  // headless mode (tests / no scene attached)

    // 2. Flash — only if caller passed a target
    if (preset.flash && opts.flashTarget) {
      try { flashWhite(scene, opts.flashTarget as never); } catch { /* test harness / wrong target type */ }
    }

    // 3. Particles — only if caller passed coords
    if (preset.particles && typeof opts.x === 'number' && typeof opts.y === 'number') {
      const color = opts.particleColor ?? this._particleColorFromName(preset.particles);
      try { explosionParticles(scene, opts.x, opts.y, color); } catch { /* test harness */ }
    } else if (preset.particles && (typeof opts.x !== 'number' || typeof opts.y !== 'number')) {
      // particles preset requested but no spawn point — silently skip.
      // G07 shipped: callers wanting boss-faction visuals call
      // `juice.spawnParticles('fire-burst'|'ice-shatter'|… , x, y)` directly.
      // eslint-disable-next-line no-console
      console.debug('[JuiceSystem.playFeedback] particles preset', preset.particles, 'requested without x/y — skipped (use spawnParticles for G07)');
    }

    // 4. Hit-pause
    if (preset.hitPauseMs > 0) {
      try { hitStop(scene, preset.hitPauseMs); } catch { /* test harness */ }
    }

    // 5. SFX — best-effort via scene._sfx (HorizontalBattleScene convention)
    if (preset.sfx) {
      try {
        const sfx = (scene as unknown as { _sfx?: { play: (key: string, vol?: number) => void } })._sfx;
        sfx?.play(preset.sfx, 0.7);
      } catch { /* AudioContext busy / no _sfx */ }
    }

    return preset;
  }

  /**
   * Map particle preset name → tint color. Keep in sync with feedback-presets.json.
   *
   * QA #C5 fix: 'spark' / unknown fallback was 0xffeb3b (yellow) — too saturated
   * and clashed with non-yellow factions. 'spark' is a generic hit indicator
   * so we use 0xeceff1 (neutral light gray-white). Named effects keep their
   * canonical colors: explosion=orange, starburst=yellow (intentional accent).
   */
  private _particleColorFromName(name: string): number {
    switch (name) {
      case 'explosion': return 0xff5722;
      case 'starburst': return 0xffeb3b;
      case 'spark': return 0xeceff1;
      default: return 0xeceff1;
    }
  }

  /**
   * G07: spawn JSON-tunable particles at (x, y). Looks up `name` in
   * particles-presets.json. Returns the spawned count (0 if unknown preset
   * or no scene attached). Boss-designer can re-tune amount/lifetime/tint
   * by editing JSON — no TS change, no rebuild (Phaser HMR reloads).
   *
   * Usage at boss attack call site:
   *   this.juice.spawnParticles('fire-burst', boss.x, boss.y);
   *
   * Replaces hardcoded emitter configs scattered across scenes.
   * Falls through to the underlying `spawnParticlesFromPreset` helper —
   * safe to call from any scene that has `add.rectangle` + `tweens.add`.
   */
  spawnParticles(name: string, x: number, y: number): number {
    const preset = PARTICLES_PRESETS[name];
    if (!preset) {
      // eslint-disable-next-line no-console
      console.warn('[JuiceSystem.spawnParticles] unknown preset:', name);
      return 0;
    }
    const scene = this.scene;
    if (!scene) return 0;  // headless mode
    return spawnParticlesFromPreset(scene, preset, x, y);
  }
}
